import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { UploadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { Form } from 'common/components';
import { BasicFormItem } from 'common/components/form/basic-form';
import { sortBy } from 'lodash';
import SnapshotFormItem from 'pages/qa-spec/components/qa-spec-tab/SnapshotFormItem';
import {
  PACKAGING_LIMIT_CHARACTERS,
  PACKAGING_LIMIT_CHARACTERS_HBC_OTC_TYPE,
  DAIRY_LIMIT_CHARACTERS
} from 'pages/qa-spec/constant';
import { useGetEnumOptions } from 'pages/qa-spec/hooks';
import { getSnapshotQaSpecPackaging } from 'services/product';
import CertificateFormList from './CertificateFormList';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';
import { renderPopoverContent } from 'pages/qa-spec/utils';
import { getFormattedValue } from 'pages/qa-spec/utils/index';

import isEmpty from 'lodash/isEmpty';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';
import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import {
  checkEnabledDietaryCertificate,
  mappingLabelingFields,
  parsePackagingData,
} from './utils';

import { checkHbcOtcType } from 'utils/qa-spec/checkTypeQaSpec';

import { TOOlTIP_FIELDS } from 'static/tooltipFields';

const makeSecondaryLanguageOption = (option) => {
  return {
    value: option?.enumCode,
    displayName: `${option.enumDescription
      } - (${option.enumCode.toUpperCase()})`,
  };
};

export const useGetQaPackagingFormDefine = ({
  handleToggleUploadDocumentModal,
  setUploadModalTitle,
  snapshotData,
  supplierModifiedData,
  productFull,
}) => {
  const packagingEnumOptions = useGetEnumOptions({
    optionMapping: useMemo(
      () => (option) => ({
        ...option,
        value: option.enumDisplayName,
        displayName: option.enumDisplayName,
      }),
      []
    ),
  })?.packaging;

  const { id: productId } = useParams();

  const isHbcOtcType = useIsHbcOtcType(productId);

  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const { isLoading } = useGetSnapshotQaPackaging({
    enabled: !!productId && isToggleSnapshot,
    productId,
  });

  const enumType = {
    uom: packagingEnumOptions.uom,
    frontOfPackIcons: packagingEnumOptions.frontOfPackIcons,
    recyclable: packagingEnumOptions.recyclabel,
    qrCode: packagingEnumOptions.qrCode,
    bilingual: packagingEnumOptions.bilingual,
    hasFoodGradePlasticsCertifier:
      packagingEnumOptions.hasFoodGradePlasticsCertifier,
    recycledMaterial: packagingEnumOptions.recycledMaterial,
    soyInk: packagingEnumOptions.soyInk,
    secondaryLanguage: packagingEnumOptions.secondaryLanguage,
    vegetableBasedInk: packagingEnumOptions.vegetableBasedInk,
  };

  const getSecondaryLanguageFormatted = (snapshotData, name) => {
    if (!snapshotData?.length) return 'N/A';
    const arrList = snapshotData.reduce((accumulator, currentValue) => {
      const currentEnum = enumType[name]?.find(
        (item) => currentValue === item.enumCode
      );

      accumulator.push(
        `${currentEnum?.enumDescription
        } - (${currentEnum?.enumCode?.toUpperCase()})`
      );

      return accumulator;
    }, []);
    return arrList.join(', ');
  };

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const isToggleRequiredFields = !isEmpty(requiredFields);

  const checkFieldRequired = (fieldName) => {
    const isAWGOwnerRequired =
      fieldName === 'productContainsBioengineeredIngredients' &&
      productFull?.ownerId === +process.env.REACT_APP_AWG_ID;

    if (isAWGOwnerRequired) return true;

    if (!requiredFields?.length) return false;
    //* fieldFullPath of cookingInstructions is same as in Fresh Produce
    const isCookingInstructionsRequired =
      fieldName === 'cookingInstructions'
        ? requiredFields.includes(
          `qaSpecification.qaSpecFreshProduce.cookingInstructions`.toLowerCase()
        )
        : false;

    const isNormalFieldRequired = requiredFields.includes(
      `qaSpecification.qaSpecPackaging.${fieldName}`.toLowerCase()
    );
    const isTdcFieldRequired = requiredFields.includes(
      `tdcProperties.${fieldName}`.toLowerCase()
    );

    return (
      isNormalFieldRequired ||
      isTdcFieldRequired ||
      isCookingInstructionsRequired
    );
  };

  const getRequiredRule = ({ fieldName, displayName }) => {
    return {
      required: checkFieldRequired(fieldName),
      message: (displayName || 'This field') + ' is required',
    };
  };

  const qaPackagingFormDefine = useMemo(() => {
    const formProductDefine = [
      {
        name: 'productName',
        label: 'Product Name:',
        fieldPath: 'qaSpecification.labeling.productName',
        layout: 'horizontal',
        labelCol: { flex: '170px' },
        disabled: true,
        supplierModifiedData:
          supplierModifiedData?.['productName']?.trim() ?? '',
        snapshotData: snapshotData?.['productName']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Product Name',
          snapshotData?.['productName']?.trim() ?? '',
          supplierModifiedData?.['productName']?.trim() ?? ''
        ),
        rules: [
          getRequiredRule({
            fieldName: 'productName',
            displayName: 'Product Name:',
          }),
        ],
      },
      {
        name: 'upc12',
        label: 'UPC12:',
        fieldPath: 'qaSpecification.labeling.upc12',
        inputProps: {
          style: { width: '100%' },
          min: '0',
          onKeyPress: (event) => {
            if (!/[0-9]/.test(event.key)) {
              event.preventDefault();
            }
          },
        },
        layout: 'horizontal',
        labelCol: { flex: '170px' },
        disabled: true,
        supplierModifiedData: supplierModifiedData?.['upc12']?.trim() ?? '',
        snapshotData: snapshotData?.['upc12']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'UPC12',
          snapshotData?.['upc12']?.trim() ?? '',
          supplierModifiedData?.['upc12']?.trim() ?? ''
        ),
        rules: [
          getRequiredRule({
            fieldName: 'upc12',
            displayName: 'UPC12',
          }),
        ],
      },
      {
        name: 'truncatedUpc',
        label: 'Truncated UPC:',
        fieldPath: 'qaSpecification.labeling.truncatedUpc',
        rules: [
          getRequiredRule({
            fieldName: 'truncatedUpc',
            displayName: 'Truncated UP:',
          }),
        ],
        type: 'numeric',
        layout: 'horizontal',
        labelCol: { flex: '170px' },
        inputProps: {
          style: { width: '100%' },
          min: '0',
          onKeyPress: (event) => {
            if (!/[0-9]/.test(event.key)) {
              event.preventDefault();
            }
          },
        },
        supplierModifiedData: supplierModifiedData?.['truncatedUpc'],
        snapshotData: snapshotData?.['truncatedUpc'],
        renderPopoverContent: renderPopoverContent(
          'Truncated UPC',
          snapshotData?.['truncatedUpc'],
          supplierModifiedData?.['truncatedUpc']
        ),
      },
      {
        name: 'secondaryDescriptor',
        label: 'Secondary Descriptor:',
        fieldPath: 'qaSpecification.labeling.secondaryDescriptor',
        rules: [
          getRequiredRule({
            fieldName: 'secondaryDescriptor',
            displayName: 'Secondary Descriptor:',
          }),
        ],
        layout: 'horizontal',
        labelCol: { flex: '170px' },
        supplierModifiedData:
          supplierModifiedData?.['secondaryDescriptor']?.trim() ?? '',
        snapshotData: snapshotData?.['secondaryDescriptor']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Secondary Descriptor',
          snapshotData?.['secondaryDescriptor']?.trim() ?? '',
          supplierModifiedData?.['secondaryDescriptor']?.trim() ?? ''
        ),
      },
      {
        name: 'tertiaryDescriptor',
        label: 'Tertiary Descriptor:',
        fieldPath: 'qaSpecification.labeling.tertiaryDescriptor',
        rules: [
          getRequiredRule({
            fieldName: 'tertiaryDescriptor',
            displayName: 'Tertiary Descriptor:',
          }),
        ],
        layout: 'horizontal',
        labelCol: { flex: '170px' },
        supplierModifiedData:
          supplierModifiedData?.['tertiaryDescriptor']?.trim() ?? '',
        snapshotData: snapshotData?.['tertiaryDescriptor']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Tertiary Descriptor',
          snapshotData?.['tertiaryDescriptor']?.trim() ?? '',
          supplierModifiedData?.['tertiaryDescriptor']?.trim() ?? ''
        ),
      },
      [
        { rowConfig: { gutter: [15, 15] } },
        {
          name: 'size',
          label: 'Size:',
          fieldPath: 'qaSpecification.labeling.size',
          rules: [
            getRequiredRule({
              fieldName: 'size',
              displayName: 'Size',
            }),
          ],
          type: 'numeric',
          layout: 'horizontal',
          labelCol: { flex: '170px' },
          disabled: true,
          colProps: {
            xs: 12,
          },
          inputProps: {
            style: { width: '100%' },
            min: 0,
          },
          style: { flexWrap: 'nowrap' },
          supplierModifiedData: supplierModifiedData?.['size'],
          snapshotData: snapshotData?.['size'],
          renderPopoverContent: renderPopoverContent(
            'Size',
            snapshotData?.['size'],
            supplierModifiedData?.['size']
          ),
        },
        {
          name: 'uom',
          label: 'UOM:',
          fieldPath: 'qaSpecification.labeling.uom',
          rules: [
            getRequiredRule({
              fieldName: 'uom',
              displayName: 'UOM',
            }),
          ],
          type: 'select',
          selectProps: {
            options: sortBy(packagingEnumOptions.uom, ['displayName']),
            inputProps: {
              allowClear: true,
              showSearch: true,
            },
          },
          layout: 'horizontal',
          labelCol: { flex: '90px' },
          disabled: true,
          colProps: {
            xs: 12,
          },
          formattedValue: getFormattedValue(
            snapshotData?.['uom'],
            'uom',
            enumType
          ),
          supplierModifiedData: supplierModifiedData?.['uom'],
          snapshotData: snapshotData?.['uom'],
          renderPopoverContent: renderPopoverContent(
            'UOM',
            snapshotData?.['uom'],
            supplierModifiedData?.['uom'],
            getFormattedValue(snapshotData?.['uom'], 'uom', enumType),
            null
          ),
        },
      ],
    ];

    const formLeftBottomDefine = [
      {
        name: 'ingredientStatement',
        label: 'Ingredient Statement',
        fieldPath: 'qaSpecification.labeling.ingredientStatement',
        rules: [
          getRequiredRule({
            fieldName: 'ingredientStatement',
            displayName: 'Ingredient Statement',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 5 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.ingredientStatement,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['ingredientStatement'] ?? '',
        snapshotData: snapshotData?.['ingredientStatement'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Ingredient Statement',
          snapshotData?.['ingredientStatement'] ?? '',
          supplierModifiedData?.['ingredientStatement'] ?? ''
        ),
      },
      {
        name: 'allergenStatement',
        label: 'Allergen Statement',
        fieldPath: 'qaSpecification.labeling.allergenStatement',
        rules: [
          getRequiredRule({
            fieldName: 'allergenStatement',
            displayName: 'Allergen Statement',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.allergenStatement,
          showCount: true,
        },
        supplierModifiedData: supplierModifiedData?.['allergenStatement'] ?? '',
        snapshotData: snapshotData?.['allergenStatement'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Allergen Statement',
          snapshotData?.['allergenStatement'] ?? '',
          supplierModifiedData?.['allergenStatement'] ?? ''
        ),
      },
      {
        name: 'storageInstructions',
        label: isHbcOtcType
          ? mappingLabelingFields['storageInstructions']?.displayName
          : 'Storage Instructions',
        fieldPath: 'qaSpecification.labeling.storageInstructions',
        rules: [
          getRequiredRule({
            fieldName: 'storageInstructions',
            displayName: isHbcOtcType
              ? mappingLabelingFields['storageInstructions']?.displayName
              : 'Storage Instructions',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.storageInstructions,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['storageInstructions'] ?? '',
        snapshotData: snapshotData?.['storageInstructions'] ?? '',
        renderPopoverContent: renderPopoverContent(
          isHbcOtcType
            ? mappingLabelingFields['storageInstructions']?.displayName
            : 'Storage Instructions',
          snapshotData?.['storageInstructions'] ?? '',
          supplierModifiedData?.['storageInstructions'] ?? ''
        ),
      },
      {
        name: 'handlingInstructions',
        label: 'Handling Instructions',
        fieldPath: 'qaSpecification.labeling.handlingInstructions',
        rules: [
          getRequiredRule({
            fieldName: 'handlingInstructions',
            displayName: 'Handling Instructions',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.handlingInstructions,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['handlingInstructions'] ?? '',
        snapshotData: snapshotData?.['handlingInstructions'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Handling Instructions',
          snapshotData?.['handlingInstructions'] ?? '',
          supplierModifiedData?.['handlingInstructions'] ?? ''
        ),
      },
      {
        name: 'thawingInstructions',
        label: 'Thawing Instructions',
        fieldPath: 'qaSpecification.labeling.thawingInstructions',
        rules: [
          getRequiredRule({
            fieldName: 'thawingInstructions',
            displayName: 'Thawing Instructions',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.thawingInstructions,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['thawingInstructions'] ?? '',
        snapshotData: snapshotData?.['thawingInstructions'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Thawing Instructions',
          snapshotData?.['thawingInstructions'] ?? '',
          supplierModifiedData?.['thawingInstructions'] ?? ''
        ),
      },
      {
        name: 'productContainsBioengineeredIngredients',
        label: 'Product Contains Bioengineered Ingredients',
        fieldPath:
          'qaSpecification.labeling.productContainsBioengineeredIngredients',
        type: 'boolean-radio',
        // rules: [
        //   {
        //     required: checkFieldRequired(
        //       'productContainsBioengineeredIngredients'
        //     ),
        //     message: 'Product Contains Bioengineered Ingredients is required',
        //   },
        // ],
        selectProps: {
          options: packagingEnumOptions.productContainsBioengineeredIngredients,
          inputProps: {
            allowClear: true,
          },
        },
        formattedValue: getFormattedValue(
          snapshotData?.['productContainsBioengineeredIngredients'],
          'productContainsBioengineeredIngredients',
          enumType
        ),
        supplierModifiedData:
          supplierModifiedData?.['productContainsBioengineeredIngredients'],
        snapshotData: snapshotData?.['productContainsBioengineeredIngredients'],
        renderPopoverContent: renderPopoverContent(
          'Product Contains Bioengineered Ingredients',
          snapshotData?.['productContainsBioengineeredIngredients'],
          supplierModifiedData?.['productContainsBioengineeredIngredients'],
          getFormattedValue(
            snapshotData?.['productContainsBioengineeredIngredients'],
            'productContainsBioengineeredIngredients',
            enumType
          ),
          'boolean-radio'
        ),
      },
      {
        name: 'bioengineeredStatement',
        label: 'Bioengineered Ingredients Statement',
        fieldPath: 'qaSpecification.labeling.bioengineeredStatement',
        rules: [
          getRequiredRule({
            fieldName: 'bioengineeredStatement',
            displayName: 'Bioengineered Ingredients Statement',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.bioengineeredStatement,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['bioengineeredStatement'] ?? '',
        snapshotData: snapshotData?.['bioengineeredStatement'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Bioengineered Ingredients Statement',
          snapshotData?.['bioengineeredStatement'] ?? '',
          supplierModifiedData?.['bioengineeredStatement'] ?? ''
        ),
      },
      {
        name: 'warningStatement',
        label: 'Warning / Caution Statement',
        fieldPath: 'qaSpecification.labeling.warningStatement',
        rules: [
          getRequiredRule({
            fieldName: 'warningStatement',
            displayName: 'Warning / Caution Statement',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: isHbcOtcType ? 6 : 3 },
          maxLength: isHbcOtcType
            ? PACKAGING_LIMIT_CHARACTERS_HBC_OTC_TYPE.warningStatement
            : PACKAGING_LIMIT_CHARACTERS.warningStatement,
          showCount: true,
        },
        supplierModifiedData: supplierModifiedData?.['warningStatement'] ?? '',
        snapshotData: snapshotData?.['warningStatement'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Warning / Caution Statement',
          snapshotData?.['warningStatement'] ?? '',
          supplierModifiedData?.['warningStatement'] ?? ''
        ),
      },
      [
        { rowConfig: { gutter: [15, 15] } },
        {
          name: 'frontOfPackIcons',
          label: 'Front of Pack Icons',
          fieldPath: 'qaSpecification.labeling.frontOfPackIcons',
          rules: [
            getRequiredRule({
              fieldName: 'frontOfPackIcons',
              displayName: 'Front of Pack Icons',
            }),
          ],
          type: 'boolean-radio',
          selectProps: {
            options: packagingEnumOptions.frontOfPackIcons,
            inputProps: {
              allowClear: true,
            },
          },
          colProps: { xs: 12 },
          formattedValue: getFormattedValue(
            snapshotData?.['frontOfPackIcons'],
            'frontOfPackIcons',
            enumType
          ),
          supplierModifiedData: supplierModifiedData?.['frontOfPackIcons'],
          snapshotData: snapshotData?.['frontOfPackIcons'],
          renderPopoverContent: renderPopoverContent(
            'Front of Pack Icons',
            snapshotData?.['frontOfPackIcons'],
            supplierModifiedData?.['frontOfPackIcons'],
            getFormattedValue(
              snapshotData?.['frontOfPackIcons'],
              'frontOfPackIcons',
              enumType
            ),
            'boolean-radio'
          ),
        },
        {
          noStyle: true,
          shouldUpdate: (prevValues, currentValues) =>
            prevValues?.['frontOfPackIcons'] !==
            currentValues?.['frontOfPackIcons'],
          renderInput:
            ({ allowEdit }) =>
              ({ getFieldValue }) => {
                const frontOfPackIconsValue = getFieldValue('frontOfPackIcons');

                return (
                  <Form.Item name='frontPackageIcons-upload-btn' label=' '>
                    <Button
                      type=''
                      danger
                      icon={<UploadOutlined />}
                      disabled={
                        frontOfPackIconsValue === 'No' ||
                        !frontOfPackIconsValue ||
                        !allowEdit
                      }
                      onClick={() => {
                        handleToggleUploadDocumentModal();
                        setUploadModalTitle(
                          'Upload Front of Pack Icons Document'
                        );
                      }}
                    >
                      Upload
                    </Button>
                  </Form.Item>
                );
              },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15] } },
        {
          name: 'recyclable',
          label: 'Recyclable',
          fieldPath: 'qaSpecification.labeling.recyclable',
          rules: [
            getRequiredRule({
              fieldName: 'recyclable',
              displayName: 'Recyclable',
            }),
          ],
          type: 'boolean-radio',
          selectProps: {
            options: packagingEnumOptions.recyclabel,
            inputProps: {
              allowClear: true,
            },
          },
          formattedValue: getFormattedValue(
            snapshotData?.['recyclable'],
            'recyclable',
            enumType
          ),
          supplierModifiedData: supplierModifiedData?.['recyclable'],
          snapshotData: snapshotData?.['recyclable'],
          renderPopoverContent: renderPopoverContent(
            'Recyclable',
            snapshotData?.['recyclable'],
            supplierModifiedData?.['recyclable'],
            getFormattedValue(
              snapshotData?.['recyclable'],
              'recyclable',
              enumType
            ),
            'boolean-radio'
          ),
          colProps: { xs: 12 },
        },
        {
          noStyle: true,
          shouldUpdate: (prevValues, currentValues) =>
            prevValues?.['recyclable'] !== currentValues?.['recyclable'],
          renderInput:
            ({ allowEdit }) =>
              ({ getFieldValue }) => {
                const recyclableValue = getFieldValue('recyclable');

                const disabled = recyclableValue === 'No' || !recyclableValue;

                const ignoredCheckingRequire = disabled;

                return (
                  <SnapshotFormItem
                    supplierModifiedData={
                      supplierModifiedData?.['recyclableTypes']
                    }
                    snapshotData={snapshotData?.['recyclableTypes']}
                    isLoadingSnapshot={isLoading}
                    renderPopoverContent={renderPopoverContent(
                      '',
                      snapshotData?.['recyclableTypes'],
                      supplierModifiedData?.['recyclableTypes']
                    )}
                  >
                    <BasicFormItem
                      name='recyclableTypes'
                      label={
                        <QaSpecHelpContentLabel fieldPath='qaSpecification.labeling.recyclableTypes'>
                          Recyclable Types
                        </QaSpecHelpContentLabel>
                      }
                      rules={
                        ignoredCheckingRequire
                          ? [
                            {
                              required: false,
                            },
                          ]
                          : [
                            getRequiredRule({
                              fieldName: 'recyclableTypes',
                              displayName: 'Recyclable Types',
                            }),
                          ]
                      }
                      type='select'
                      selectProps={{
                        inputProps: {
                          disabled,
                          allowClear: true,
                          mode: 'multiple',
                        },
                        options: sortBy(packagingEnumOptions.recyclableType, [
                          'displayName',
                        ]),
                      }}
                      allowEdit={allowEdit}
                    />
                  </SnapshotFormItem>
                );
              },
          colProps: { xs: 12 },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15] } },
        {
          name: 'totalRecyclableContentPercentage',
          label: 'Total Recycled Content Percentage',
          fieldPath:
            'qaSpecification.labeling.totalRecyclableContentPercentage',
          rules: [
            getRequiredRule({
              fieldName: 'totalRecyclableContentPercentage',
              displayName: 'Total Recycled Content Percentage',
            }),
          ],
          type: 'numeric',
          colProps: { xs: 12 },
          inputProps: { style: { width: '100%' } },
          supplierModifiedData:
            supplierModifiedData?.['totalRecyclableContentPercentage'],
          snapshotData: snapshotData?.['totalRecyclableContentPercentage'],
          renderPopoverContent: renderPopoverContent(
            'Total Recycled Content Percentage',
            snapshotData?.['totalRecyclableContentPercentage'],
            supplierModifiedData?.['totalRecyclableContentPercentage']
          ),
        },
        {
          name: 'postConsumerRecycledContentPercentage',
          label: 'Post Consumer Recycled Content Percentage',
          fieldPath:
            'qaSpecification.labeling.postConsumerRecycledContentPercentage',
          rules: [
            getRequiredRule({
              fieldName: 'postConsumerRecycledContentPercentage',
              displayName: 'Post Consumer Recycled Content Percentage',
            }),
          ],
          type: 'numeric',
          colProps: { xs: 12 },
          inputProps: { style: { width: '100%' } },
          supplierModifiedData:
            supplierModifiedData?.['postConsumerRecycledContentPercentage'],
          snapshotData: snapshotData?.['postConsumerRecycledContentPercentage'],
          renderPopoverContent: renderPopoverContent(
            'Post Consumer Recycled Content Percentage',
            snapshotData?.['postConsumerRecycledContentPercentage'],
            supplierModifiedData?.['postConsumerRecycledContentPercentage']
          ),
        },
      ],
      {
        name: 'packagingType',
        label: 'Packaging Type',
        fieldPath: 'qaSpecification.labeling.packagingType',
        rules: [
          getRequiredRule({
            fieldName: 'packagingType',
            displayName: 'Packaging Type',
          }),
        ],
        type: 'select',
        selectProps: {
          options: sortBy(packagingEnumOptions.packagingType, ['displayName']),
          inputProps: {
            allowClear: true,
            showSearch: true,
          },
        },
        supplierModifiedData: supplierModifiedData?.['packagingType'],
        snapshotData: snapshotData?.['packagingType'],
        renderPopoverContent: renderPopoverContent(
          'Packaging Type',
          snapshotData?.['packagingType'],
          supplierModifiedData?.['packagingType']
        ),
      },
      {
        name: 'packagingMaterial',
        label: 'Packaging Material',
        fieldPath: 'qaSpecification.labeling.packagingMaterial',
        rules: [
          getRequiredRule({
            fieldName: 'packagingMaterial',
            displayName: 'Packaging Material',
          }),
        ],
        type: 'select',
        selectProps: {
          options: sortBy(packagingEnumOptions.packagingMaterial, [
            'displayName',
          ]),
          inputProps: {
            allowClear: true,
            showSearch: true,
          },
        },
        supplierModifiedData: supplierModifiedData?.['packagingMaterial'],
        snapshotData: snapshotData?.['packagingMaterial'],
        renderPopoverContent: renderPopoverContent(
          'Packaging Material',
          snapshotData?.['packagingMaterial'],
          supplierModifiedData?.['packagingMaterial']
        ),
      },
      [
        { rowConfig: { gutter: [15, 15] } },
        {
          name: 'qrCode',
          label: 'QR Code',
          fieldPath: 'qaSpecification.labeling.qrCode',
          rules: [
            getRequiredRule({
              fieldName: 'qrCode',
              displayName: 'QR Code',
            }),
          ],
          type: 'boolean-radio',
          selectProps: {
            options: packagingEnumOptions.qrCode,
            inputProps: {
              allowClear: true,
            },
          },
          formattedValue: getFormattedValue(
            snapshotData?.['qrCode'],
            'qrCode',
            enumType
          ),
          supplierModifiedData: supplierModifiedData?.['qrCode'],
          snapshotData: snapshotData?.['qrCode'],
          renderPopoverContent: renderPopoverContent(
            'QR Code',
            snapshotData?.['qrCode'],
            supplierModifiedData?.['qrCode'],
            getFormattedValue(snapshotData?.['qrCode'], 'qrCode', enumType),
            'boolean-radio'
          ),
          colProps: { xs: 12 },
        },
        {
          noStyle: true,
          shouldUpdate: (prevValues, currentValues) =>
            prevValues?.['qrCode'] !== currentValues?.['qrCode'],
          renderInput:
            ({ allowEdit }) =>
              ({ getFieldValue }) => {
                const qrCodeValue = getFieldValue('qrCode');

                return (
                  <Form.Item name='qrCode-upload-btn' label=' '>
                    <Button
                      type=''
                      danger
                      icon={<UploadOutlined />}
                      disabled={
                        qrCodeValue === 'No' || !qrCodeValue || !allowEdit
                      }
                      onClick={() => {
                        handleToggleUploadDocumentModal();
                        setUploadModalTitle('Upload QR Code Document');
                      }}
                    >
                      Upload
                    </Button>
                  </Form.Item>
                );
              },
        },
        {
          name: 'bilingual',
          label: 'Additional Languages on Label',
          fieldPath: 'qaSpecification.labeling.bilingual',
          rules: [
            getRequiredRule({
              fieldName: 'bilingual',
              displayName: 'Additional Languages on Label',
            }),
          ],
          type: 'boolean-radio',
          selectProps: {
            options: packagingEnumOptions.bilingual,
            inputProps: {
              allowClear: true,
            },
          },
          formattedValue: getFormattedValue(
            snapshotData?.['bilingual'],
            'bilingual',
            enumType
          ),
          supplierModifiedData: supplierModifiedData?.['bilingual'],
          snapshotData: snapshotData?.['bilingual'],
          renderPopoverContent: renderPopoverContent(
            'Additional Languages on Label',
            snapshotData?.['bilingual'],
            supplierModifiedData?.['bilingual'],
            getFormattedValue(
              snapshotData?.['bilingual'],
              'bilingual',
              enumType
            ),
            'boolean-radio'
          ),
          colProps: { xs: 12 },
        },
        {
          noStyle: true,
          shouldUpdate: (prevValues, currentValues) =>
            prevValues?.['bilingual'] !== currentValues?.['bilingual'],
          renderInput:
            ({ allowEdit }) =>
              ({ getFieldValue }) => {
                const bilingualValue = getFieldValue('bilingual');

                const disabled = bilingualValue === 'No' || !bilingualValue;

                const ignoredCheckingRequire = disabled;

                return (
                  <SnapshotFormItem
                    supplierModifiedData={
                      supplierModifiedData?.['secondaryLanguage']
                    }
                    snapshotData={snapshotData?.['secondaryLanguage']}
                    isLoadingSnapshot={isLoading}
                    renderPopoverContent={renderPopoverContent(
                      'Secondary Language',
                      snapshotData?.['secondaryLanguage'],
                      supplierModifiedData?.['secondaryLanguage'],
                      getSecondaryLanguageFormatted(
                        snapshotData?.['secondaryLanguage'],
                        'secondaryLanguage'
                      ),
                      null
                    )}
                  >
                    <BasicFormItem
                      name='secondaryLanguage'
                      label={
                        <QaSpecHelpContentLabel fieldPath='qaSpecification.labeling.secondaryLanguage'>
                          Secondary Language
                        </QaSpecHelpContentLabel>
                      }
                      rules={
                        ignoredCheckingRequire
                          ? [{ required: false }]
                          : [
                            getRequiredRule({
                              fieldName: 'secondaryLanguage',
                              displayName: 'Secondary Language',
                            }),
                          ]
                      }
                      type='select'
                      selectProps={{
                        inputProps: {
                          showSearch: true,
                          allowClear: true,
                          disabled,
                          mode: 'multiple',
                        },
                        options: sortBy(
                          packagingEnumOptions.secondaryLanguage?.map((option) =>
                            makeSecondaryLanguageOption(option)
                          ),
                          ['displayName']
                        ),
                      }}
                      allowEdit={allowEdit}
                    />
                  </SnapshotFormItem>
                );
              },
          colProps: { xs: 12 },
        },
      ],
    ];

    const formProductClaimFirstSectionDefine = [
      {
        name: 'claim1',
        label: 'Claim 1:',
        fieldPath: 'qaSpecification.labeling.claim1',
        rules: [
          getRequiredRule({ fieldName: 'claim1', displayName: 'Claim 1' }),
        ],
        layout: 'horizontal',
        supplierModifiedData: supplierModifiedData?.['claim1']?.trim() ?? '',
        snapshotData: snapshotData?.['claim1']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Claim 1',
          snapshotData?.['claim1']?.trim() ?? '',
          supplierModifiedData?.['claim1']?.trim() ?? ''
        ),
      },
      {
        name: 'claim2',
        label: 'Claim 2:',
        fieldPath: 'qaSpecification.labeling.claim2',
        rules: [
          getRequiredRule({ fieldName: 'claim2', displayName: 'Claim 2' }),
        ],
        layout: 'horizontal',
        supplierModifiedData: supplierModifiedData?.['claim2']?.trim() ?? '',
        snapshotData: snapshotData?.['claim2']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Claim 2',
          snapshotData?.['claim2']?.trim() ?? '',
          supplierModifiedData?.['claim2']?.trim() ?? ''
        ),
      },
      {
        name: 'claim3',
        label: 'Claim 3:',
        fieldPath: 'qaSpecification.labeling.claim3',
        rules: [
          getRequiredRule({ fieldName: 'claim3', displayName: 'Claim 3' }),
        ],
        layout: 'horizontal',
        supplierModifiedData: supplierModifiedData?.['claim3']?.trim() ?? '',
        snapshotData: snapshotData?.['claim3']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Claim 3',
          snapshotData?.['claim3']?.trim() ?? '',
          supplierModifiedData?.['claim3']?.trim() ?? ''
        ),
      },
      {
        name: 'claim4',
        label: 'Claim 4:',
        fieldPath: 'qaSpecification.labeling.claim4',
        rules: [
          getRequiredRule({ fieldName: 'claim4', displayName: 'Claim 4' }),
        ],
        layout: 'horizontal',
        supplierModifiedData: supplierModifiedData?.['claim4']?.trim() ?? '',
        snapshotData: snapshotData?.['claim4']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Claim 4',
          snapshotData?.['claim4']?.trim() ?? '',
          supplierModifiedData?.['claim4']?.trim() ?? ''
        ),
      },
      {
        name: 'claim5',
        label: 'Claim 5:',
        fieldPath: 'qaSpecification.labeling.claim5',
        rules: [
          getRequiredRule({ fieldName: 'claim5', displayName: 'Claim 5' }),
        ],
        layout: 'horizontal',
        supplierModifiedData: supplierModifiedData?.['claim5']?.trim() ?? '',
        snapshotData: snapshotData?.['claim5']?.trim() ?? '',
        renderPopoverContent: renderPopoverContent(
          'Claim 5',
          snapshotData?.['claim5']?.trim() ?? '',
          supplierModifiedData?.['claim5']?.trim() ?? ''
        ),
      },
    ];

    const formRightSecondSection = [
      {
        name: 'romanceCopy',
        label: 'Romance Copy',
        fieldPath: 'qaSpecification.labeling.romanceCopy',
        rules: [
          getRequiredRule({
            fieldName: 'romanceCopy',
            displayName: 'Romance Copy',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.romanceCopy,
          showCount: true,
        },
        supplierModifiedData: supplierModifiedData?.['romanceCopy'] ?? '',
        snapshotData: snapshotData?.['romanceCopy'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Romance Copy',
          snapshotData?.['romanceCopy'] ?? '',
          supplierModifiedData?.['romanceCopy'] ?? ''
        ),
      },
      {
        name: 'usage',
        label: isHbcOtcType
          ? mappingLabelingFields['usage']?.displayName
          : 'Usage',
        fieldPath: 'qaSpecification.labeling.usage',
        type: 'textarea',
        rules: [
          {
            required: checkFieldRequired('usage'),
            message: `${isHbcOtcType
              ? mappingLabelingFields['usage']?.displayName
              : 'Usage'
              } is required`,
          },
        ],
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.usage,
          showCount: true,
        },
        supplierModifiedData: supplierModifiedData?.['usage'] ?? '',
        snapshotData: snapshotData?.['usage'] ?? '',
        renderPopoverContent: renderPopoverContent(
          isHbcOtcType ? mappingLabelingFields['usage']?.displayName : 'Usage',
          snapshotData?.['usage'] ?? '',
          supplierModifiedData?.['usage'] ?? ''
        ),
      },
      {
        name: 'cookingInstructions',
        label: isHbcOtcType
          ? mappingLabelingFields['cookingInstructions']?.displayName
          : 'Cooking Instructions/ Directions',
        fieldPath: 'qaSpecification.labeling.cookingInstructions',
        rules: [
          getRequiredRule({
            fieldName: 'cookingInstructions',
            displayName: isHbcOtcType
              ? mappingLabelingFields['cookingInstructions']?.displayName
              : 'Cooking Instructions/ Directions',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: isHbcOtcType ? 6 : 3 },
          maxLength: isHbcOtcType
            ? PACKAGING_LIMIT_CHARACTERS_HBC_OTC_TYPE.cookingInstructions
            : PACKAGING_LIMIT_CHARACTERS.cookingInstructions,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['cookingInstructions'] ?? '',
        snapshotData: snapshotData?.['cookingInstructions'] ?? '',
        renderPopoverContent: renderPopoverContent(
          isHbcOtcType
            ? mappingLabelingFields['cookingInstructions']?.displayName
            : 'Cooking Instructions/ Directions',
          snapshotData?.['cookingInstructions'] ?? '',
          supplierModifiedData?.['cookingInstructions'] ?? ''
        ),
      },
      {
        name: 'recipe',
        label: 'Recipe',
        fieldPath: 'qaSpecification.labeling.recipe',
        rules: [
          getRequiredRule({
            fieldName: 'recipe',
            displayName: 'Recipe',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.recipe,
          showCount: true,
        },
        supplierModifiedData: supplierModifiedData?.['recipe'] ?? '',
        snapshotData: snapshotData?.['recipe'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Recipe',
          snapshotData?.['recipe'] ?? '',
          supplierModifiedData?.['recipe'] ?? ''
        ),
      },
      {
        name: 'healthClaims',
        label: 'Health Claims',
        fieldPath: 'qaSpecification.labeling.healthClaims',
        rules: [
          getRequiredRule({
            fieldName: 'healthClaims',
            displayName: 'Health Claims',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: DAIRY_LIMIT_CHARACTERS.healthClaims,
          showCount: true,
        },
        supplierModifiedData: supplierModifiedData?.['healthClaims'] ?? '',
        snapshotData: snapshotData?.['healthClaims'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Health Claims',
          snapshotData?.['healthClaims'] ?? '',
          supplierModifiedData?.['healthClaims'] ?? ''
        ),
      },
    ];

    const formDietaryThirdSectionDefine = [
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'kosherYN',
          label: 'Kosher:',
          fieldPath: 'qaSpecification.labeling.kosherYN',
          type: 'boolean-radio',
          supplierModifiedData: supplierModifiedData?.['kosherYN'],
          snapshotData: snapshotData?.['kosherYN'],
          renderPopoverContent: renderPopoverContent(
            'Kosher',
            snapshotData?.['kosherYN'],
            supplierModifiedData?.['kosherYN'],
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          layout: 'horizontal',
          labelCol: {
            flex: '120px',
          },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'organicYN',
          label: 'Organic:',
          fieldPath: 'qaSpecification.labeling.organicYN',
          type: 'boolean-radio',
          layout: 'horizontal',
          supplierModifiedData: supplierModifiedData?.['organicYN'],
          snapshotData: snapshotData?.['organicYN'],
          renderPopoverContent: renderPopoverContent(
            'Organic',
            snapshotData?.['organicYN'],
            supplierModifiedData?.['organicYN'],
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          labelCol: {
            flex: '120px',
          },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'halalYN',
          label: 'Halal:',
          fieldPath: 'qaSpecification.labeling.halalYN',
          type: 'boolean-radio',
          supplierModifiedData: supplierModifiedData?.['halalYN'],
          snapshotData: snapshotData?.['halalYN'],
          renderPopoverContent: renderPopoverContent(
            'Halal',
            snapshotData?.['halalYN'],
            supplierModifiedData?.['halalYN'],
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          layout: 'horizontal',
          labelCol: {
            flex: '120px',
          },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'nonGMOYN',
          label: 'Non-GMO:',
          fieldPath: 'qaSpecification.labeling.nonGMOYN',
          // type: 'checkbox',
          // valuePropName: 'checked',
          type: 'boolean-radio',
          supplierModifiedData: supplierModifiedData?.['nonGMOYN'],
          snapshotData: snapshotData?.['nonGMOYN'],
          renderPopoverContent: renderPopoverContent(
            'Non-GMO',
            snapshotData?.['nonGMOYN'],
            supplierModifiedData?.['nonGMOYN'],
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          layout: 'horizontal',
          labelCol: {
            flex: '120px'
          },
        },
      ],

    ];

    const formFacilityCertificationsSectionDefine = [
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'isoyn',
          label: 'ISO:',
          fieldPath: 'qaSpecification.labeling.isoyn',
          // type: 'checkbox',
          // valuePropName: 'checked',
          type: 'boolean-radio',
          layout: 'horizontal',
          supplierModifiedData: supplierModifiedData?.['isoyn'] ?? false,
          snapshotData: snapshotData?.['isoyn'] ?? false,
          renderPopoverContent: renderPopoverContent(
            'ISO',
            snapshotData?.['isoyn'] ?? false,
            supplierModifiedData?.['isoyn'] ?? false,
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          labelCol: {
            flex: '120px',
          },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'brcyn',
          label: 'BRC:',
          fieldPath: 'qaSpecification.labeling.brcyn',
          // type: 'checkbox',
          // valuePropName: 'checked',
          type: 'boolean-radio',
          layout: 'horizontal',
          supplierModifiedData: supplierModifiedData?.['brcyn'] ?? false,
          snapshotData: snapshotData?.['brcyn'] ?? false,
          renderPopoverContent: renderPopoverContent(
            'BRC',
            snapshotData?.['brcyn'] ?? false,
            supplierModifiedData?.['brcyn'] ?? false,
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          labelCol: {
            flex: '120px',
          },
        },
      ],
      [
        { rowConfig: { gutter: [15, 15], style: { marginBottom: 10 } } },
        {
          name: 'ifsyn',
          label: 'IFS:',
          fieldPath: 'qaSpecification.labeling.ifsyn',
          // type: 'checkbox',
          // valuePropName: 'checked',
          type: 'boolean-radio',
          layout: 'horizontal',
          supplierModifiedData: supplierModifiedData?.['ifsyn'],
          snapshotData: snapshotData?.['ifsyn'],
          renderPopoverContent: renderPopoverContent(
            'IFS',
            snapshotData?.['ifsyn'],
            supplierModifiedData?.['ifsyn'],
            null,
            'boolean-radio'
          ),
          colProps: {
            xs: 24,
          },
          labelCol: {
            flex: '120px',
          },
        },
      ],
    ];

    const formRightFourthSectionDefine = [
      {
        name: 'otherCertifications',
        label: 'Other Certifications',
        fieldPath: 'qaSpecification.labeling.otherCertifications',
        rules: [
          getRequiredRule({
            fieldName: 'otherCertifications',
            displayName: 'Other Certifications',
          }),
        ],
        type: 'textarea',
        inputProps: {
          autoSize: { minRows: 3, maxRows: 3 },
          maxLength: PACKAGING_LIMIT_CHARACTERS.otherCertifications,
          showCount: true,
        },
        supplierModifiedData:
          supplierModifiedData?.['otherCertifications'] ?? '',
        snapshotData: snapshotData?.['otherCertifications'] ?? '',
        renderPopoverContent: renderPopoverContent(
          'Other Certifications',
          snapshotData?.['otherCertifications'] ?? '',
          supplierModifiedData?.['otherCertifications'] ?? ''
        ),
      },
      {
        name: 'hasFoodGradePlasticsCertifier',
        label: 'Contains Food Grade Plastics',
        fieldPath: 'qaSpecification.labeling.hasFoodGradePlasticsCertifier',
        rules: [
          getRequiredRule({
            fieldName: 'hasFoodGradePlasticsCertifier',
            displayName: 'Contains Food Grade Plastics',
          }),
        ],
        type: 'boolean-radio',
        selectProps: {
          options: packagingEnumOptions.hasFoodGradePlasticsCertifier,
          inputProps: {
            allowClear: true,
          },
        },
        formattedValue: getFormattedValue(
          snapshotData?.['hasFoodGradePlasticsCertifier'],
          'hasFoodGradePlasticsCertifier',
          enumType
        ),
        supplierModifiedData:
          supplierModifiedData?.['hasFoodGradePlasticsCertifier'],
        snapshotData: snapshotData?.['hasFoodGradePlasticsCertifier'],
        renderPopoverContent: renderPopoverContent(
          'Contains Food Grade Plastics',
          snapshotData?.['hasFoodGradePlasticsCertifier'],
          supplierModifiedData?.['hasFoodGradePlasticsCertifier'],
          getFormattedValue(
            snapshotData?.['hasFoodGradePlasticsCertifier'],
            'hasFoodGradePlasticsCertifier',
            enumType
          ),
          'boolean-radio'
        ),
      },
      {
        name: 'recycledMaterial',
        label: 'Made With Recycled Material',
        fieldPath: 'qaSpecification.labeling.recycledMaterial',
        rules: [
          getRequiredRule({
            fieldName: 'recycledMaterial',
            displayName: 'Made With Recycled Material',
          }),
        ],
        type: 'boolean-radio',
        selectProps: {
          options: packagingEnumOptions.recycledMaterial,
          inputProps: {
            allowClear: true,
          },
        },
        formattedValue: getFormattedValue(
          snapshotData?.['recycledMaterial'],
          'recycledMaterial',
          enumType
        ),
        supplierModifiedData: supplierModifiedData?.['recycledMaterial'],
        snapshotData: snapshotData?.['recycledMaterial'],
        renderPopoverContent: renderPopoverContent(
          'Made With Recycled Material',
          snapshotData?.['recycledMaterial'],
          supplierModifiedData?.['recycledMaterial'],
          getFormattedValue(
            snapshotData?.['recycledMaterial'],
            'recycledMaterial',
            enumType
          ),
          'boolean-radio'
        ),
      },
      {
        name: 'soyInk',
        label: 'Soy Ink',
        fieldPath: 'qaSpecification.labeling.soyInk',
        rules: [
          getRequiredRule({
            fieldName: 'soyInk',
            displayName: 'Soy Ink',
          }),
        ],
        type: 'boolean-radio',
        selectProps: {
          options: packagingEnumOptions.soyInk,
          inputProps: {
            allowClear: true,
          },
        },
        formattedValue: getFormattedValue(
          snapshotData?.['soyInk'],
          'soyInk',
          enumType
        ),
        supplierModifiedData: supplierModifiedData?.['soyInk'],
        snapshotData: snapshotData?.['soyInk'],
        renderPopoverContent: renderPopoverContent(
          'Soy Ink',
          snapshotData?.['soyInk'],
          supplierModifiedData?.['soyInk'],
          getFormattedValue(snapshotData?.['soyInk'], 'soyInk', enumType),
          'boolean-radio'
        ),
      },
      {
        name: 'vegetableBasedInk',
        label: 'Vegetable Based Ink ',
        fieldPath: 'qaSpecification.labeling.vegetableBasedInk',
        rules: [
          getRequiredRule({
            fieldName: 'vegetableBasedInk',
            displayName: 'Vegetable Based Ink ',
          }),
        ],
        type: 'boolean-radio',
        selectProps: {
          options: packagingEnumOptions.vegetableBasedInk,
          inputProps: {
            allowClear: true,
          },
        },
        formattedValue: getFormattedValue(
          snapshotData?.['vegetableBasedInk'],
          'vegetableBasedInk',
          enumType
        ),
        supplierModifiedData: supplierModifiedData?.['vegetableBasedInk'],
        snapshotData: snapshotData?.['vegetableBasedInk'],
        renderPopoverContent: renderPopoverContent(
          'Vegetable Based Ink',
          snapshotData?.['vegetableBasedInk'],
          supplierModifiedData?.['vegetableBasedInk'],
          getFormattedValue(
            snapshotData?.['vegetableBasedInk'],
            'vegetableBasedInk',
            enumType
          ),
          'boolean-radio'
        ),
      },
    ];

    return {
      formProductDefine,
      formProductClaimFirstSectionDefine,
      formRightSecondSection,
      formLeftBottomDefine,
      formDietaryThirdSectionDefine,
      formRightFourthSectionDefine,
      formFacilityCertificationsSectionDefine
    };
  }, [
    packagingEnumOptions,
    setUploadModalTitle,
    supplierModifiedData,
    snapshotData,
    enumType,
    getSecondaryLanguageFormatted,
    handleToggleUploadDocumentModal,
    isToggleRequiredFields,
    isLoading,
  ]);

  return qaPackagingFormDefine;
};

const getSelectedCertifiers = ({ getFieldValue }) => {
  return [
    ...(getFieldValue('kosherCertifier') ?? []),
    ...(getFieldValue('organicCertifier') ?? []),
    ...(getFieldValue('halalCertifier') ?? []),
    ...(getFieldValue('nonGMOCertifier') ?? []),
  ];
};

const getQueryKeyQaPackaging = (productId) => {
  return ['pim', 'snapshot', parseInt(productId), 'qa-packaging'];
};

export const useGetSnapshotQaPackaging = ({
  enabled,
  productId,
  ...restProps
}) => {
  const queryClient = useQueryClient();

  const getSnapshotQaPackagingData = async () => {
    const response = await getSnapshotQaSpecPackaging({ productId });
    const { isSuccess, data } = response;
    if (!isSuccess) return null;

    return parsePackagingData(data);
  };

  const query = useQuery({
    queryKey: getQueryKeyQaPackaging(productId),
    queryFn: getSnapshotQaPackagingData,
    enabled,
    ...restProps,
  });

  const invalidateSnapshotQaSpecPackaging = () => {
    queryClient.invalidateQueries({
      queryKey: getQueryKeyQaPackaging(productId),
    });
  };

  return { ...query, invalidateSnapshotQaSpecPackaging };
};

export const useIsHbcOtcType = (productId) => {
  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: !!productId,
  });

  return checkHbcOtcType(qaSpecDataHeader?.type);
};
